import type { AiFeature, AiProvider, Effort } from './types';

// The AI models Clientbook offers, with list prices in US dollars per million tokens.
// Prices checked against the providers' published price lists on 17 Oct 2026.
// The super admin can correct any price in the admin console if a provider changes it.

export interface AiPrice {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Dollars per 1,000 web searches. */
  webSearchPer1k: number;
  /** Rates that apply to the whole request once the prompt is longer than `threshold` tokens. */
  long?: { threshold: number; input: number; output: number; cacheRead: number; cacheWrite: number };
}

export interface AiModelInfo {
  id: string;
  provider: AiProvider;
  name: string;
  /** One line on what the model is good for. */
  blurb: string;
  price: AiPrice;
  efforts: Effort[];
}

export const AI_MODELS: AiModelInfo[] = [
  {
    id: 'claude-opus-5-5',
    provider: 'anthropic',
    name: 'Claude Opus 5.5',
    blurb: 'Most capable for research. Best results in the Finder.',
    price: { input: 4, output: 20, cacheRead: 0.2, cacheWrite: 5, webSearchPer1k: 10 },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'claude-sonnet-5-5',
    provider: 'anthropic',
    name: 'Claude Sonnet 5.5',
    blurb: 'Strong and half the price of Opus. A good default.',
    price: { input: 2, output: 10, cacheRead: 0.1, cacheWrite: 2.5, webSearchPer1k: 10 },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'claude-haiku-5-5',
    provider: 'anthropic',
    name: 'Claude Haiku 5.5',
    blurb: 'Fast and very cheap. Suits email triage.',
    price: {
      input: 0.1,
      output: 0.5,
      cacheRead: 0.01,
      cacheWrite: 0.125,
      webSearchPer1k: 10,
      long: { threshold: 100_000, input: 0.5, output: 2.5, cacheRead: 0.05, cacheWrite: 0.625 },
    },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'claude-fable-5-1',
    provider: 'anthropic',
    name: 'Claude Fable 5.1',
    blurb: 'Top tier. Rarely worth the price for CRM work.',
    price: { input: 10, output: 50, cacheRead: 0.25, cacheWrite: 12.5, webSearchPer1k: 10 },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'gpt-6.1-sol',
    provider: 'openai',
    name: 'GPT-6.1 Sol',
    blurb: 'Balanced quality and price. A good default.',
    price: {
      input: 2,
      output: 10,
      cacheRead: 0.1,
      cacheWrite: 2.5,
      webSearchPer1k: 10,
      long: { threshold: 272_000, input: 4, output: 15, cacheRead: 0.2, cacheWrite: 5 },
    },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'gpt-6-astra',
    provider: 'openai',
    name: 'GPT-6 Astra',
    blurb: 'Flagship. Most thorough research, highest price.',
    price: {
      input: 10,
      output: 50,
      cacheRead: 1,
      cacheWrite: 12.5,
      webSearchPer1k: 10,
      long: { threshold: 272_000, input: 20, output: 75, cacheRead: 2, cacheWrite: 25 },
    },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'gpt-6-sol',
    provider: 'openai',
    name: 'GPT-6 Sol',
    blurb: 'Previous mid tier, same price as 6.1 Sol.',
    price: {
      input: 2,
      output: 10,
      cacheRead: 0.2,
      cacheWrite: 2.5,
      webSearchPer1k: 10,
      long: { threshold: 272_000, input: 4, output: 15, cacheRead: 0.4, cacheWrite: 5 },
    },
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: 'gpt-6-luna',
    provider: 'openai',
    name: 'GPT-6 Luna',
    blurb: 'Small and very cheap. Suits email triage.',
    price: {
      input: 0.1,
      output: 0.5,
      cacheRead: 0.01,
      cacheWrite: 0.125,
      webSearchPer1k: 10,
      long: { threshold: 272_000, input: 0.2, output: 0.75, cacheRead: 0.02, cacheWrite: 0.25 },
    },
    efforts: ['low', 'medium', 'high'],
  },
];

export interface AiFeatureSetting {
  provider: AiProvider;
  model: string;
  effort: Effort;
}

export const AI_FEATURE_LABELS: Record<AiFeature, { name: string; detail: string }> = {
  finder: { name: 'AI Finder', detail: 'Searches the web for companies that match a description, a lookalike or filters.' },
  fill: { name: 'Fill details', detail: 'Looks up missing details on companies and people, such as website, size, job title and LinkedIn profile, for you to approve.' },
  triage: { name: 'Analyse email', detail: 'Reads a triage email on request and suggests who it belongs to.' },
};

export const DEFAULT_FEATURE_MODELS: Record<AiProvider, Record<AiFeature, AiFeatureSetting>> = {
  anthropic: {
    finder: { provider: 'anthropic', model: 'claude-sonnet-5-5', effort: 'high' },
    fill: { provider: 'anthropic', model: 'claude-sonnet-5-5', effort: 'medium' },
    triage: { provider: 'anthropic', model: 'claude-haiku-5-5', effort: 'low' },
  },
  openai: {
    finder: { provider: 'openai', model: 'gpt-6.1-sol', effort: 'high' },
    fill: { provider: 'openai', model: 'gpt-6.1-sol', effort: 'medium' },
    triage: { provider: 'openai', model: 'gpt-6-luna', effort: 'low' },
  },
};
